import React, { useState, useRef, useEffect } from "react";
import AuthentifiedPage from "./AuthentifiedPage";
import { useLocation } from "react-router-dom";
import { Indicateur } from "../types/Indicateur";
import { indicateurService } from "../services/IndicateurService";
import { useAuth } from "../context/AuthContext";
import { Contenu } from "../types/Contenu";
import { getBaseTypeById } from "../types/BaseTypes";
import { getFieldTypeById } from "../types/FieldTypes";

/****************************** TYPES ******************************/
interface Colonne { name: string; }
interface FormulePart { motCle: string; colonne: string; operation?: string; }

const IndicateurForm: React.FC = () => {
  const location = useLocation();
  const { domain, sousDomain, sousSousDomain } = location.state || {};
  const { api } = useAuth();

  const tableName =
    sousSousDomain?.label ||
    sousDomain?.label ||
    domain?.label ||
    "Indicateur";

  /*************** STATES *********************/
  const [nom, setNom] = useState("");
  const [unite, setUnite] = useState(""); 
  const [formuleParts, setFormuleParts] = useState<FormulePart[]>([]);
  const [indicateurs, setIndicateurs] = useState<Indicateur[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [whereParts, setWhereParts] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [colonnesForm, setColonnesForm] = useState<Colonne[]>([]);
  const [contenus, setContenus] = useState<Contenu[] | null>(null);
  const [groupByColumns, setGroupByColumns] = useState<string[]>([]);

  useEffect(() => {
    loadIndicateurs();
  }, [sousDomain, sousSousDomain]);

  const loadIndicateurs = async () => {
    if (!api) {
      return;
    }

    try {
      setLoading(true);
      const data = await indicateurService.getAllIndicateurs(api);

      const mappedData = data.map((ind: any) => ({
        id: ind.idIndicateur || ind.id,
        nom: ind.nom,
        formule: ind.formule,
        sql: ind.sql || ind.formule,
        tableName: ind.tableName || ind.table_name || "N/A",
        valeur: ind.valeur || 0,
        isAccepted:
          ind.isAccepted !== undefined
            ? ind.isAccepted
            : ind.is_accepted !== undefined
            ? ind.is_accepted
            : false,
        idFenetre: ind.idFenetre ?? ind.id_fenetre ?? null,
        idSousFenetre: ind.idSousFenetre ?? ind.id_sous_fenetre ?? null,
        unite: ind.unite ?? null,
        createdAt: ind.createdAt || ind.created_at,
        reference: ind.reference
      }));

      setIndicateurs(mappedData);

    } catch (err: any) {
      alert("Impossible de charger les indicateurs");
      setIndicateurs([]);
    } finally {
      setLoading(false);
    }
  };

  const colonnes: Colonne[] = colonnesForm;

  const fonctions = ["SOMME", "MOYENNE", "NOMBRE"];
  const operations = ["+", "-", "*", "/", "(", ")"];

  const insertAtCursor = (text: string) =>
    setFormuleParts((prev) => [...prev, { motCle: "", colonne: text }]);

  const getFormule = () =>
    formuleParts
      .map((p) =>
        p.motCle ? `${p.motCle}(${p.colonne})`
        : p.operation ? p.operation
        : p.colonne
      )
      .join(" ");

  //ACTIVATION DU GROUP BY
  const toggleGroupByColumn = (colName: string) => {
    setGroupByColumns((prev) =>
      prev.includes(colName)
        ? prev.filter((c) => c !== colName) // désactiver
        : [...prev, colName]                // activer
    );
  };

  /*************** RÉCUPÉRATION DU FORMULAIRE *********************/
  useEffect(() => {
    const loadColonnes = async () => {
      if (!api) return;

      const idFenetre = sousDomain?.id;
      const idSousFenetre = sousSousDomain?.id;

      if (!idFenetre && !idSousFenetre) return;

      try {
        const response = await indicateurService.getContenus(
          api,
          idFenetre,
          idSousFenetre
        );

        const repairedResponse=response.data?.map((contenu)=>{
          contenu.baseType=getBaseTypeById(contenu.typeBase);
          contenu.type=getFieldTypeById(contenu.typeChamp);
          return contenu;
        })

        setContenus(repairedResponse??[]);

        if (response.success && response.data) {
          const cols = response.data.map((c) => ({
            name: c.nomContenu
          }));

          setColonnesForm(cols);
        }
      } catch (err) {
      }
    };

    loadColonnes();
  }, [api, sousDomain, sousSousDomain]);

  /*************** SAUVEGARDE INDICATEUR *********************/
  const saveIndicateur = async () => {
    if (!nom.trim()) {
      alert("Entrez un nom");
      return;
    }

    const formuleHuman = getFormule();

    if (!formuleHuman.trim()) {
      alert("Veuillez créer une formule");
      return;
    }

    const formuleValidationError = indicateurService.validateFormuleNumericFields(
      formuleHuman,
      contenus?contenus:[],
      fonctions,
      operations
    );

    if (formuleValidationError) {
      alert(formuleValidationError); 
      return;
    }

    const idFenetre: number | null = sousDomain?.id ?? null;
    const idSousFenetre: number | null = sousSousDomain?.id ?? null;

    const newInd: Indicateur = {
      id: Date.now(),
      nom,
      formule: formuleHuman,
      tableName,
      valeur: 0,    
      isAccepted: true,
      idFenetre,
      idSousFenetre,
      unite: unite || null,
      createdAt: new Date().toISOString(),
      reference: groupByColumns.length>0?groupByColumns.join(","):null
    };

    if(loading){
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await indicateurService.createIndicateur(api, newInd);
      
      if (response.success) {
        // Mise à jour de la liste locale
        setIndicateurs((prev) => [...prev, newInd]);
        
        // Message de succès
        alert(response.message || "Indicateur créé avec succès");
        
        // Reset du formulaire
        setNom("");
        setUnite("");
        setFormuleParts([]);
        setGroupBy([]);
        setWhereParts([]);
        
        // Recharger la liste depuis l'API
        loadIndicateurs();
      } else {
        setError(response.message || "Erreur lors de la création");
        alert(response.message || "Erreur lors de la création");
      }
    } catch (err: any) {
      let errorMessage = "Erreur lors de la sauvegarde";
      
      if (err.response?.status === 403) {
        errorMessage = "Accès refusé. Veuillez vérifier vos permissions ou vous reconnecter.";
      } else if (err.response?.status === 401) {
        errorMessage = "Session expirée. Veuillez vous reconnecter.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /*************** STYLE *********************/
  const btnStyle = (id: string, active: boolean = false) => ({
    padding: "6px 10px",
    margin: "5px",
    borderRadius: "5px",
    cursor: "pointer",
    border: "1px solid #ddd",
    background: active ? "#222" : "#fafafa",
    color: active ? "white" : "black",
    transition: "0.2s",
  });

  /*************** RENDER *********************/
  return (
    <AuthentifiedPage>
      <div style={{ display: "flex", maxWidth: 1000, margin: "20px auto", fontFamily: "Arial", gap: 20 }}>
        <div style={{ flex: 2 }}>
          <h3>
            Créer un indicateur : <span style={{ color: "green" }}> {tableName}</span>
          </h3>

          {error && (
            <div style={{ padding: 10, marginBottom: 10, background: "#fee", color: "#c00", borderRadius: 5 }}>
              {error}
            </div>
          )}

          {/* NOM */}
          <div style={{ marginBottom: 20 }}>
            <label>Nom :</label>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              style={{ width: "100%", padding: 8, marginTop: 5 }}
              disabled={loading}
            />
          </div>

          {/* UNITE */}
          <div style={{ marginBottom: 20 }}>
            <label>Unité :</label>
            <input
              type="text"
              value={unite}
              onChange={(e) => setUnite(e.target.value)}
              placeholder="Ex: Kg, L, %"
              style={{ width: "100%", padding: 8, marginTop: 5 }}
              disabled={loading}
            />
          </div>

          {/* FONCTIONS */}
          <div style={{ marginBottom: 20 }}>
            <label>Fonctions :</label>
            <div style={{ display: "flex", marginTop: 10 }}>
              {fonctions.map((f) => (
                <button 
                  key={f} 
                  style={btnStyle(f)} 
                  onClick={() => insertAtCursor(f + "(")}
                  disabled={loading}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* COLONNES */}
          <div style={{ marginBottom: 20 }}>
            <label>Colonnes :</label>
            <div style={{ display: "flex", flexWrap: "wrap", marginTop: 10 }}>
              {colonnes.map((c) => (
                <button 
                  key={c.name} 
                  style={btnStyle(c.name)} 
                  onClick={() => insertAtCursor(c.name)}
                  disabled={loading}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* OPERATIONS */}
          <div style={{ marginBottom: 20 }}>
            <label>Opérations :</label>
            <div style={{ display: "flex", marginTop: 10 }}>
              {operations.map((op) => (
                <button 
                  key={op} 
                  style={btnStyle(op)} 
                  onClick={() => insertAtCursor(op)}
                  disabled={loading}
                >
                  {op}
                </button>
              ))}
            </div>
          </div>

          {/* FORMULE */}
          <div style={{ marginBottom: 20 }}>
            <label>Formule :</label>
            <input
              type="text"
              ref={inputRef}
              value={getFormule()}
              onChange={(e) => setFormuleParts([{ motCle: "", colonne: e.target.value }])}
              style={{ width: "100%", padding: 8, marginTop: 5 }}
              disabled={loading}
            />
          </div>

          {/* GROUP BY */}
          <div style={{ marginBottom: 20 }}>
            <label>Références :</label>
            <div style={{ display: "flex", flexWrap: "wrap", marginTop: 10 }}>
              {colonnes.map((c) => {
                const active = groupByColumns.includes(c.name);
                return (
                  <button
                    key={c.name}
                    style={btnStyle(c.name, active)}
                    onClick={() => toggleGroupByColumn(c.name)}
                    disabled={loading}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>


          {/* ENREGISTRER */}
          <button
            onClick={saveIndicateur}
            disabled={loading}
            style={{
              marginTop: 25,
              padding: "10px 20px",
              background: loading ? "#ccc" : "#428652ff",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: "bold",
            }}
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>

        {/* LISTE DES INDICATEURS */}
        <div style={{ flex: 1, border: "1px solid #ddd", padding: 10, borderRadius: 6 }}>
          <h3>Indicateurs</h3>

          {loading && <div style={{ textAlign: "center", padding: 20 }}>Chargement...</div>}

          {indicateurs.map((i) => (
            <div key={i.id} style={{ marginBottom: 10, padding: 5, background: "#f9f9f9", borderRadius: 4 }}>
              <strong>{i.nom}</strong> {i.unite && `(${i.unite})`}
              <div style={{ fontSize: 12 }}>Formule: {i.formule}</div>
              <div style={{ fontSize: 12 }}>Colonne(s) de référence: {i.reference}</div>
            </div>
          ))}
        </div>
      </div>
    </AuthentifiedPage>
  );
};

export default IndicateurForm;